import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../context/useThemeContext";
import { hexToRGBA } from "../../utility/hexToRGB";
import useAbsoluteWindowStore from "../../stores/absoluteWindowStore";
import { useGetEditor } from "../../context/useEditorContext";

function HyperlinkMenu() {
    const { theme } = useTheme();
    const { windowShowing, position } = useAbsoluteWindowStore(
        (state) => state
    );
    const editor = useGetEditor().editor.current;
    const overrideApply = useRef(false);

    const menuRef = useRef<HTMLDivElement>(null);
    const linkRef = useRef<HTMLInputElement>(null);
    const textRef = useRef<HTMLInputElement>(null);

    // Set Data on start to refs
    useEffect(() => {
        if (!editor?.state || !linkRef.current || !textRef.current) return;

        // Title
        const { from, to } = editor?.state.selection!;
        const selectedText = editor?.state.doc.textBetween(from, to, " ");

        textRef.current!.value = selectedText!;

        // Link
        const attr = editor?.getAttributes("link");
        linkRef.current!.value = attr?.href || "";
    }, [editor?.state, linkRef, textRef]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setData();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuRef]);

    function setData() {
        // If escape was hit
        if (overrideApply.current) {
            const { from, to } = editor?.state.selection!;
            const selectedText = editor?.state.doc.textBetween(from, to, " ");
            const attr = editor?.getAttributes("link");

            linkRef.current!.value = attr?.href || "";
            textRef.current!.value = selectedText || "";
            overrideApply.current = false;

            useAbsoluteWindowStore.getState().setWindowShowing(undefined);
            return;
        }

        useAbsoluteWindowStore.getState().setWindowShowing(undefined);

        if (editor?.state.selection.empty) return;
        if (linkRef.current!.value === "") {
            editor?.commands.unsetLink();
        } else {
            const { from, to } = editor?.state.selection!;
            editor
                ?.chain()
                .focus()
                .deleteRange({ from, to }) // Remove old link text
                .insertContentAt(from, {
                    type: "text",
                    text: textRef.current!.value,
                    marks: [
                        {
                            type: "link",
                            attrs: {
                                href: linkRef.current!.value,
                            },
                        },
                    ],
                })
                .run();
        }
        overrideApply.current = false;
    }

    return (
        <>
            {windowShowing && windowShowing.type === "Link" && (
                <div
                    ref={menuRef}
                    className="absolute gap-2 flex flex-col items-center justify-center z-[999]"
                    style={{
                        width: "306px",
                        padding: "4px 8px",
                        borderRadius: "8px",
                        backgroundColor: hexToRGBA(theme.mainContentColor, 0.9), // theme.mainContentColor,
                        border: `1px solid  ${hexToRGBA(
                            theme.fontColor[100],
                            0.2
                        )}`,
                        backdropFilter: "blur(5px)",
                        top: Math.max(
                            20,
                            Math.min(position.y - 35, window.innerHeight - 100)
                        ),
                        // 160 is to account for width AND translate x -50%
                        left: Math.max(
                            160,
                            Math.min(position.x, window.innerWidth - 170)
                        ),
                        transform: "translate(-50%, 0%)",
                    }}
                >
                    <div className="flex gap-3 justify-center items-center">
                        <p className="text-sm">Text</p>
                        <input
                            ref={textRef}
                            placeholder="..."
                            type="text"
                            className="w-[250px] bg-transparent rounded-md outline-none text-sm px-2 py-1"
                            style={{
                                backgroundColor: `${hexToRGBA(
                                    theme.headerColor,
                                    0.7
                                )}`,
                            }}
                            onFocus={(e) => {
                                // Select all
                                const input = e.target as HTMLInputElement;
                                input.focus();
                                input.setSelectionRange(0, input.value.length);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    overrideApply.current = false;
                                    setData();
                                    // (e.target as HTMLInputElement).blur();
                                } else if (e.key === "Escape") {
                                    overrideApply.current = true;
                                    setData();
                                    useAbsoluteWindowStore
                                        .getState()
                                        .setWindowShowing(undefined);
                                }
                            }}
                        />
                    </div>
                    <div className="flex gap-3 justify-center items-center">
                        <p className="text-sm">Link</p>
                        <input
                            ref={linkRef}
                            placeholder="..."
                            type="text"
                            className="w-[250px] bg-transparent rounded-md outline-none text-sm px-2 py-1"
                            style={{
                                backgroundColor: `${hexToRGBA(
                                    theme.headerColor,
                                    0.7
                                )}`,
                            }}
                            autoFocus
                            onFocus={(e) => {
                                const attr = editor?.getAttributes("link");
                                e.target.value = attr?.href || "";

                                // Select all
                                const input = e.target as HTMLInputElement;
                                input.focus();
                                input.setSelectionRange(0, input.value.length);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    overrideApply.current = false;
                                    (e.target as HTMLInputElement).blur();
                                } else if (e.key === "Escape") {
                                    overrideApply.current = true;
                                    (e.target as HTMLInputElement).blur();
                                }
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

export default HyperlinkMenu;
